import { CodeBracketSquareIcon } from "@components/library/Icons";
import { ContextMenuListItem } from "@components/library/Lists/ContextMenuListItem";
import { VariableIcon } from "@heroicons/react/outline";
import * as Popover from "@radix-ui/react-popover";
import { EditorState, SelectionState } from "draft-js";
import { Dispatch, SetStateAction, memo, useCallback, useEffect, useMemo, useState } from "react";
import { OrgToolConfig } from "services/v4.service";
import { useEventListener } from "usehooks-ts";
import { InputTag, ToolTag } from "./VariableTags";
import { addTextAtCursor, deleteTextAtCursor } from "./utils";

export interface InputSuggestion {
  type: "input";
  name: string;
  description?: string;
}
interface ToolSuggestion {
  type: "tool";
  tool: OrgToolConfig;
}
type Suggestion = InputSuggestion | ToolSuggestion;

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editorState: EditorState;
  setEditorState: (editorState: EditorState) => void;
  /** List of available tools */
  availableTools?: OrgToolConfig[];
  /** List of available input names */
  availableVariables?: InputSuggestion[];
  /** Toggles whether this responds to keystrokes */
  enabled: boolean;
}

type Position = { top: number; left: number };

const AutocompletePanel = ({
  open,
  setOpen,
  editorState,
  setEditorState,
  enabled,
  availableTools,
  availableVariables,
}: Props) => {
  const [positionStyles, setPositionStyles] = useState<Position | null>(null);

  const selectionState = editorState.getSelection();

  // TODO: This is fixed relative to the viewport, and it doesn't update when the user scrolls, or the layout changes.
  // We should fix this. Ideas:
  // Look at how @mentions are done https://www.draft-js-plugins.com/plugin/mention
  // Use relative position to the editor, and use Radix portal which automatically moves the element to the trigger.
  useEffect(() => {
    const positionStyles = getPositionStyles(selectionState);
    if (positionStyles !== null) {
      setPositionStyles(positionStyles);
    }
  }, [selectionState]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [typedString, setTypedString] = useState("");

  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedIndex(0);
    setTypedString("");
  }, [setOpen]);

  const suggestions: Suggestion[] = useMemo(() => {
    const filteredInputs = availableVariables?.filter((input) => input.name.startsWith(typedString)) || [];
    const filteredTools =
      availableTools
        ?.filter((tool) => tool.config?.signature?.startsWith(typedString))
        // We don't allow json_schema tools to be used in the playground since they have no runtime
        .filter((tool) => tool.config?.preset_name !== "json_schema") || [];
    return [...filteredInputs, ...(filteredTools.map((tool) => ({ type: "tool", tool })) as ToolSuggestion[])];
  }, [availableVariables, availableTools, typedString]);

  const handleSuggestionSelection = useCallback(
    (suggestion: Suggestion) => {
      if (suggestion === undefined) {
        return;
      }
      const newEditorState = addToEditorState(suggestion, typedString.length, editorState);
      setEditorState(newEditorState);
      handleClose();
    },
    [editorState, handleClose, setEditorState, typedString.length],
  );

  const keydownEventHandler = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) {
        return;
      }
      if (event.key === "{") {
        const { text, offset } = getBlockTextAndOffset(editorState);
        if (text[offset - 1] === "{") {
          setOpen(true);
          return;
        }
      }

      // Manually trigger autocomplete box with ctrl-space.
      if (event.ctrlKey && event.key === " ") {
        const { text, offset } = getBlockTextAndOffset(editorState);
        if (text.slice(0, offset).includes("{{")) {
          // Try autocomplete from last {{
          const split = text.slice(0, offset).split("{{");
          const textOnRightOfBracket = split[split.length - 1];
          setTypedString(textOnRightOfBracket);
          setOpen(true);
          return;
        }
      }
      if (!open) {
        return;
      }

      if (event.key === "Backspace" || event.key === "ArrowLeft") {
        if (typedString.length > 0) {
          setTypedString((typedString) => typedString.slice(0, typedString.length - 1));
        } else {
          handleClose();
        }
      }
      if (event.key === "ArrowRight") {
        const { text, offset } = getBlockTextAndOffset(editorState);
        setTypedString((typedString) => typedString + text[offset]);
      }

      if (event.key === "Escape") {
        handleClose();
      } else if (event.key === "ArrowUp") {
        setSelectedIndex((selectedIndex) => Math.max(selectedIndex - 1, 0));
      } else if (event.key === "ArrowDown") {
        setSelectedIndex((selectedIndex) => Math.min(selectedIndex + 1, suggestions.length - 1));
      }
      if (event.key === "Enter" || event.key === "Tab") {
        handleSuggestionSelection(suggestions[selectedIndex]);
      }
      if (event.key.length === 1) {
        setTypedString((typedString) => `${typedString}${event.key}`);
        setSelectedIndex(0);
      }
    },
    [
      editorState,
      enabled,
      handleSuggestionSelection,
      handleClose,
      open,
      selectedIndex,
      setOpen,
      suggestions,
      typedString.length,
    ],
  );

  useEventListener("keydown", keydownEventHandler);

  useEventListener("mousedown", (event) => {
    if (open) {
      const isInside = (event.target as any).closest(".hl-suggestion") !== null;
      if (!isInside) {
        handleClose();
      }
    }
  });

  useEffect(() => {
    if (suggestions.length === 0) {
      handleClose();
    }
  }, [handleClose, selectedIndex, suggestions]);

  if (!open) {
    return null;
  }

  const selectedSuggestion: Suggestion | undefined = suggestions[selectedIndex];

  return (
    <Popover.Root defaultOpen>
      <Popover.Portal>
        <div style={positionStyles || undefined} className="fixed z-10 mt-24 flex animate-fade-in-down items-start">
          <div className="min-w-[200px] overflow-hidden rounded-ms border border-oldgray-400 bg-white shadow-1">
            <div className="flex max-h-[200px] flex-col overflow-auto ">
              {suggestions.map((suggestion, i) => {
                const selected = i === selectedIndex;
                return (
                  <ContextMenuListItem
                    key={i}
                    active={selected}
                    onClick={() => {
                      handleSuggestionSelection(suggestion);
                    }}
                    size={32}
                    IconRight={
                      suggestion.type === "input"
                        ? VariableIcon
                        : suggestion.type === "tool"
                          ? CodeBracketSquareIcon
                          : undefined
                    }
                    className="hl-suggestion"
                  >
                    {suggestion.type === "input" && (
                      <span>
                        <InputTag>
                          <span className="font-bold text-black">{typedString}</span>
                          <span className="text-gray-700">{suggestion.name.slice(typedString.length)}</span>
                        </InputTag>
                      </span>
                    )}

                    {suggestion.type === "tool" &&
                      // `suggestion` is guaranteed to have a signature due to check above. Doing check again mainly for TypeScript.
                      suggestion.tool.config.signature && (
                        <span className="">
                          <ToolTag>
                            <span className="font-bold  text-black">{typedString}</span>
                            <span className="text-gray-700">
                              {suggestion.tool.config.signature.slice(typedString.length)}
                            </span>
                          </ToolTag>
                        </span>
                      )}
                  </ContextMenuListItem>
                );
              })}
            </div>
          </div>
          {selectedSuggestion?.type === "tool" && (
            <div className="max-w-[300px] rounded-ms border border-oldgray-400 bg-white font-sans shadow-1">
              <div className="flex flex-col gap-8 p-8">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-13-16">{selectedSuggestion.tool.name}</div>
                  <CodeBracketSquareIcon className="h-16 w-16 text-icon-base-3" />
                </div>
                <div className="text-11-14 text-gray-700">{selectedSuggestion.tool.config.description}</div>
              </div>
            </div>
          )}
          {selectedSuggestion?.type === "input" && selectedSuggestion?.description && (
            <div className="max-w-[300px] rounded-ms border border-oldgray-400 bg-white font-sans shadow-1">
              <div className="flex flex-col gap-8 p-8">
                {/* <div className="text-13-16">{selectedSuggestion.name}</div> */}
                <div className="flex items-center justify-between gap-4">
                  <InputTag>{selectedSuggestion.name}</InputTag>
                  <VariableIcon className="h-16 w-16 text-icon-base-3" />
                </div>
                <div className="text-11-14 text-gray-700">{selectedSuggestion.description}</div>
              </div>
            </div>
          )}
        </div>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default memo(AutocompletePanel);

const getPositionStyles = (selectionState: SelectionState) => {
  if (!selectionState.getHasFocus()) {
    // console.log("selectionState does not have focus");
    return null;
  }
  const selection = window.getSelection();
  if (selection === null) {
    console.log("Selection is null");
    return null;
  }
  if (selection.rangeCount === 0) {
    // console.log("Selection has no range");
    return null;
  }
  // Position of the cursor, relative to the viewport
  const rect = selection.getRangeAt(0).getBoundingClientRect();

  if (rect.top > 0) {
    // If we have a valid rect at the cursor, we just use that (the easy way)
    return { top: rect.top, left: rect.left };
  } else {
    // This would be the case if the cursor is at the start of a block, say (no selection).
    // rect is sometimes {x: 0, y: 0, width: 0, height: 0, top: 0, …}
    // when the cursor is at the start of a block
    const focusNode = selection.focusNode as Element;

    if (!focusNode.getBoundingClientRect) {
      // `focusNode.getBoundingClientRect is not a function` sometimes. Unsure when.
      return null;
    }
    // Client rect is relative to viewport, not page.
    const rect = focusNode.getBoundingClientRect();
    return { top: rect.top, left: rect.left };
  }
};

const addToEditorState = (suggestion: Suggestion, insertFromIndex: number, editorState: EditorState): EditorState => {
  if (suggestion.type === "tool") {
    if (insertFromIndex > 0) {
      return addTextAtCursor(
        ` ${suggestion.tool.config.signature} }}`,
        deleteTextAtCursor(insertFromIndex, editorState),
      );
    } else {
      return addTextAtCursor(` ${suggestion.tool.config.signature} }}`, editorState);
    }
  } else if (suggestion.type === "input") {
    if (insertFromIndex > 0) {
      return addTextAtCursor(` ${suggestion.name} }}`, deleteTextAtCursor(insertFromIndex, editorState));
    } else {
      return addTextAtCursor(` ${suggestion.name} }}`, editorState);
    }
  } else {
    return editorState;
  }
  return editorState;
};

/**
 * Get text in block where cursor is, as well as offset for cursor.
 */
const getBlockTextAndOffset = (editorState: EditorState): { text: string; offset: number } => {
  const selectionState = editorState.getSelection();
  const focusKey = selectionState.getFocusKey();
  const currentContent = editorState.getCurrentContent();
  const currentContentBlock = currentContent.getBlockForKey(focusKey);
  const start = selectionState.getStartOffset();
  const blockText = currentContentBlock.getText();
  return { text: blockText, offset: start };
};
